import { useEffect, useRef } from "react";
import { PiX } from "react-icons/pi";
import { lstr } from "./localization";

export function Modal({
  showCloseButton,
  locale,
  closeModal,
  children,
  height,
}: {
  showCloseButton: boolean;
  locale: string;
  closeModal: () => void;
  children: React.ReactNode;
  height?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  // Run once on mount: calling showModal() on an already-open dialog throws
  // an InvalidStateError, so this must not re-run on re-renders.
  useEffect(() => {
    ref.current?.showModal();
  }, []);

  return (
    <dialog
      // hidden until showModal() opens it: a display utility would otherwise
      // show the closed dialog in the page flow for a frame.
      className={`animate-pop-in hidden w-[480px] max-w-[calc(100%-2rem)] flex-col rounded-3xl border border-border bg-surface p-0 text-main-text shadow-pop backdrop:bg-black/40 backdrop:backdrop-blur-sm open:flex ${height ?? ""}`}
      ref={ref}
      onCancel={closeModal}
      onClick={(e) => {
        // Only a backdrop click targets the <dialog> itself: its content is
        // wrapped in child elements and the dialog has no padding of its
        // own. Comparing click coordinates with the dialog's rect instead
        // would also close it on keyboard-activated buttons, whose click
        // events report (0, 0).
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="relative flex-grow p-6">
        {showCloseButton && (
          <button
            type="button"
            aria-label={lstr(locale).close_button_caption}
            title={lstr(locale).close_button_caption}
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-secondary-text transition-colors hover:bg-cream-dark hover:text-main-text"
            onClick={closeModal}
          >
            <PiX size={18} />
          </button>
        )}
        {children}
      </div>
    </dialog>
  );
}
